import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getContrastTextColor } from "@/utils/colorUtils";

export function CheckoutTipSelector({
  tipping,
  colors,
  onTipCentsChange,
  idPrefix = "checkout",
}: {
  tipping?: {
    enabled: boolean;
    amount1?: number;
    amount2?: number;
    amount3?: number;
  };
  colors?: { accentColor?: string };
  onTipCentsChange?: ({ tipAmountCents }: { tipAmountCents: number }) => void;
  idPrefix?: string;
}) {
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState("");

  const getTipAmount = useCallback(() => {
    if (selectedTipAmount !== null) return selectedTipAmount;
    const customAmount = parseFloat(customTipAmount);
    return Number.isNaN(customAmount) || customAmount < 0 ? 0 : customAmount;
  }, [selectedTipAmount, customTipAmount]);

  const onTipChangeRef = useRef(onTipCentsChange);
  onTipChangeRef.current = onTipCentsChange;

  useEffect(() => {
    const tipCents = Math.round(getTipAmount() * 100);
    onTipChangeRef.current?.({ tipAmountCents: tipCents });
  }, [selectedTipAmount, customTipAmount, getTipAmount]);

  if (!tipping?.enabled) return null;

  const accentColor = colors?.accentColor || "#4b5563";

  return (
    <div className="py-4 space-y-4">
      <h3 className="text-lg font-semibold">Add tip</h3>
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          {tipping.amount1 !== undefined ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedTipAmount(tipping.amount1!);
                setCustomTipAmount("");
              }}
              style={
                selectedTipAmount === tipping.amount1
                  ? {
                      backgroundColor: accentColor,
                      color: getContrastTextColor(accentColor),
                    }
                  : undefined
              }
            >
              ${tipping.amount1.toFixed(2)}
            </Button>
          ) : null}
          {tipping.amount2 !== undefined ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedTipAmount(tipping.amount2!);
                setCustomTipAmount("");
              }}
              style={
                selectedTipAmount === tipping.amount2
                  ? {
                      backgroundColor: accentColor,
                      color: getContrastTextColor(accentColor),
                    }
                  : undefined
              }
            >
              ${tipping.amount2.toFixed(2)}
            </Button>
          ) : null}
          {tipping.amount3 !== undefined ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedTipAmount(tipping.amount3!);
                setCustomTipAmount("");
              }}
              style={
                selectedTipAmount === tipping.amount3
                  ? {
                      backgroundColor: accentColor,
                      color: getContrastTextColor(accentColor),
                    }
                  : undefined
              }
            >
              ${tipping.amount3.toFixed(2)}
            </Button>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-custom-tip`}>Custom tip</Label>
          <Input
            id={`${idPrefix}-custom-tip`}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={customTipAmount}
            onChange={(event) => {
              setCustomTipAmount(event.target.value);
              setSelectedTipAmount(null);
            }}
          />
        </div>
      </div>
    </div>
  );
}
